import React from 'react';
import styled from 'styled-components';
import ScrollReveal from './shared/ScrollReveal';

const Section = styled.section`
  padding: 96px 24px;
  max-width: 1120px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 64px 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 56px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.accent};
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 600;
  color: ${p => p.theme.text};
  letter-spacing: -0.03em;
  max-width: 480px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  padding: 24px;
  background: var(--tz-wash, rgba(255, 255, 255, 0.02));
  border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.06));
  border-radius: 12px;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: var(--tz-wash, rgba(255, 255, 255, 0.04));
    border-color: var(--tz-hairline-strong, rgba(255, 255, 255, 0.1));
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CategoryName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${p => p.theme.text};
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: ${p => p.theme.radiusFull};
  background: ${p => p.write ? 'rgba(246, 113, 113, 0.1)' : p.theme.greenDim};
  color: ${p => p.write ? '#F67171' : p.theme.green};
  white-space: nowrap;
`;

const ToolList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToolName = styled.div`
  font-family: ${p => p.theme.fontMono};
  font-size: 13px;
  color: ${p => p.theme.textTertiary};
  line-height: 1.6;
`;

const TeslaTag = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${p => p.theme.textTertiary};
  margin-left: 8px;
  padding: 2px 7px;
  background: var(--tz-wash, rgba(255, 255, 255, 0.04));
  border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.06));
  border-radius: 4px;
  vertical-align: middle;
`;

const CATEGORIES = [
  {
    name: 'Vehicles',
    tools: ['list_vehicles', 'get_vehicle_status'],
  },
  {
    name: 'Drives',
    tools: ['get_drives', 'get_drive_detail'],
  },
  {
    name: 'Charging',
    tools: ['get_charges', 'get_charge_detail', 'get_charge_report'],
  },
  {
    name: 'Battery Health',
    tools: ['get_battery_health'],
  },
  {
    name: 'Efficiency',
    tools: ['get_efficiency'],
  },
  {
    name: 'Road Trips',
    tools: ['get_road_trips', 'get_road_trip_detail'],
  },
  {
    name: 'Chargers',
    tools: ['get_my_chargers', 'find_nearby_chargers', 'get_charger_detail'],
  },
  {
    name: 'Charger Map',
    tools: ['search_public_chargers', 'find_food_near_charger'],
  },
  {
    name: 'Stats & Aggregations',
    tools: ['get_stats', 'get_aggregations'],
  },
  {
    name: 'Commands',
    tools: ['get_command_capabilities', 'send_vehicle_command'],
    write: true,
    note: 'Tesla only',
  },
];

export default function ToolsReference() {
  return (
    <Section id="tools">
      <ScrollReveal>
        <Header>
          <SectionLabel>Tools</SectionLabel>
          <SectionTitle>20 tools, 10 categories — and counting</SectionTitle>
        </Header>
      </ScrollReveal>
      <ScrollReveal>
        <Grid>
          {CATEGORIES.map((cat) => (
            <Card key={cat.name}>
              <CardTop>
                <CategoryName>
                  {cat.name}
                  {cat.note && <TeslaTag>{cat.note}</TeslaTag>}
                </CategoryName>
                <Badge write={cat.write}>
                  {cat.write ? 'Read + Write' : 'Read'}
                </Badge>
              </CardTop>
              <ToolList>
                {cat.tools.map(t => <ToolName key={t}>{t}</ToolName>)}
              </ToolList>
            </Card>
          ))}
        </Grid>
      </ScrollReveal>
    </Section>
  );
}
